import {
  Component,
  effect,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { NotificationService } from '@app/core/services/notification/notification.service';
import {
  NotificationSlugEnum,
  NotificationRuleSeverityEnum,
  ChannelNotification,
  PermissionEnum,
  NotificationRule,
} from '@app/generated/graphql';
import { ToastCloseEvent, Toast } from 'primeng/toast';
import { NotificationActionService } from './notification-action.service';
import { INotificationMessage } from './interface';
import { NotificationsOrchestratorService } from '@app/shared/global-layout/components/notifications-v2/notifications-orchestrator.service';
import { AlertsOrchestratorService } from '@app/shared/global-layout/components/alerts-menu/alerts-orchestrator.service';
import {
  ITrackingNotification,
  ITrackingAlert,
} from '../notification-preferences/interfaces/analytics.interface';
import { NotificationPreferencesAnalyticsService } from '../notification-preferences/services/notification-preferences-analytics.service';
import { HasPermissionDirective } from '../../core/directives/has-permission.directive';
import { Bind } from 'primeng/bind';
import { Ag1BadgeSmallComponent } from '../../core/components/ag1-badge-small/ag1-badge-small.component';
import { Button } from 'primeng/button';
import { AngularSvgIconModule } from 'angular-svg-icon';

@Component({
  selector: 'ag-notifications-and-alerts',
  templateUrl: './notifications-and-alerts.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    HasPermissionDirective,
    Bind,
    Toast,
    Ag1BadgeSmallComponent,
    Button,
    AngularSvgIconModule,
  ],
})
export class NotificationsAndAlertsComponent {
  notificationRuleSeverityEnum = NotificationRuleSeverityEnum;

  // dependencies
  private _notificationService = inject(NotificationService);
  private _notificationsOrchestratorService = inject(
    NotificationsOrchestratorService,
  );
  private _alertsOrchestratorService = inject(AlertsOrchestratorService);
  private _messageService = inject(MessageService);
  private _notificationQueue: INotificationMessage[] = [];
  private _isShowingNotification: boolean = false;
  private _notificationActionService = inject(NotificationActionService);
  private _notificationPreferencesAnalyticsService = inject(
    NotificationPreferencesAnalyticsService,
  );

  // tracks when each toast was actually displayed on screen
  private _toastDisplayTimes = new Map<string, number>();

  readonly NOTIFICATION_TOAST_KEY: string = 'notification-toast';
  readonly NEXT_NOTIFICATION_DISPLAY_DELAY: number = 300;
  readonly TOAST_EXPIRY_BUFFER_MS: number = 500;

  PermissionEnum = PermissionEnum;

  notifications = toSignal(this._notificationService.notifications$);

  constructor() {
    effect(() => {
      this.queueNotifications(this.notifications());
    });
  }

  onAction(
    metadata: Record<string, any>,
    notificationRuleSlug: NotificationSlugEnum,
  ): void {
    this._notificationActionService.executeAction(
      metadata,
      notificationRuleSlug,
    );
  }

  onClose(event: ToastCloseEvent): void {
    this._isShowingNotification = false;
    this.showNextNotification();

    const channelNotificationId: string | undefined =
      event.message?.data?.channelNotification?.id?.toString();

    const displayedAt: number | undefined = this._toastDisplayTimes.get(
      channelNotificationId,
    );

    const life: number = event.message?.life ?? 0;

    if (displayedAt) {
      const elapsed: number = Date.now() - displayedAt;
      const wasAutoExpired: boolean =
        elapsed >= life - this.TOAST_EXPIRY_BUFFER_MS;

      if (wasAutoExpired) {
        this.trackNotificationToastExpired(event, displayedAt);
      }

      this._toastDisplayTimes.delete(channelNotificationId);
    }
  }

  queueNotifications(notifications: INotificationMessage[]): void {
    if (!notifications?.length) {
      return;
    }

    this._notificationQueue.push(...notifications);

    if (!this._isShowingNotification) {
      this.showNextNotification();
    }
  }

  showNotification(notification: INotificationMessage): void {
    this._messageService.clear(this.NOTIFICATION_TOAST_KEY);

    // capture the actual display time for analytics tracking
    this._toastDisplayTimes.set(
      notification.channelNotification.id,
      Date.now(),
    );

    this._messageService.add({
      key: this.NOTIFICATION_TOAST_KEY,
      id: notification.id,
      summary: notification.displayTitle,
      detail: notification.displayDescription,
      life: notification.life,
      severity: 'custom', // primeng requirement for custom styling
      styleClass: 'backdrop-blur-lg rounded-xl',
      data: {
        notificationRuleSlug: notification.notificationRuleSlug,
        severity: notification.severity,
        actionLabel: notification.actionLabel,
        metadata: notification.metadata,
        channelNotification: notification.channelNotification,
      },
    });
  }

  showNextNotification(): void {
    if (this._notificationQueue.length > 0) {
      this._isShowingNotification = true;

      const nextNotification: INotificationMessage =
        this._notificationQueue.shift()!;

      this._notificationPreferencesAnalyticsService.trackToastDisplayed({
        notification: this._toTrackingNotification(
          nextNotification.channelNotification,
        ),
      });

      setTimeout(() => {
        this.showNotification(nextNotification);
      }, this.NEXT_NOTIFICATION_DISPLAY_DELAY);
    }
  }

  markNotificationAsRead(notification: ChannelNotification): void {
    if (!notification?.id || !notification?.notificationRule?.severity) return;

    switch (notification.notificationRule.severity) {
      case NotificationRuleSeverityEnum.Info:
        this._notificationsOrchestratorService.onMarkIncomingNotificationAsRead(
          notification,
        );
        break;
      case NotificationRuleSeverityEnum.Alert:
        this._alertsOrchestratorService.onMarkAlertAsRead(notification);
        break;
    }
  }

  trackNotificationDismissed(notificationMessage: INotificationMessage): void {
    const channelNotification: ChannelNotification | undefined =
      notificationMessage?.channelNotification;

    if (!this._hasTrackableRule(channelNotification)) return;

    const timeSinceDisplayedSeconds: number = this._getTimeSinceDisplayed(
      channelNotification.id,
    );

    this._trackBySeverity(channelNotification, {
      info: () =>
        this._notificationPreferencesAnalyticsService.trackNotificationDismissed(
          {
            notification: this._toTrackingNotification(channelNotification),
            timeSinceDisplayedSeconds,
            source: 'toast',
          },
        ),
      alert: () =>
        this._notificationPreferencesAnalyticsService.trackAlertDismissed({
          alert: this._toTrackingAlert(channelNotification),
          timeSinceDisplayedSeconds,
          source: 'toast',
        }),
    });
  }

  trackNotificationCallToActionClick(
    notificationMessage: INotificationMessage,
  ): void {
    const channelNotification: ChannelNotification | undefined =
      notificationMessage?.channelNotification;

    if (!this._hasTrackableRule(channelNotification)) return;

    this._trackBySeverity(channelNotification, {
      info: () =>
        this._notificationPreferencesAnalyticsService.trackNotificationCtaClicked(
          {
            notification: this._toTrackingNotification(channelNotification),
            ctaLabel: notificationMessage.actionLabel ?? '',
            ctaDestination: notificationMessage.metadata?.ctaDestination ?? '',
          },
        ),
      alert: () =>
        this._notificationPreferencesAnalyticsService.trackAlertCtaClicked({
          alert: this._toTrackingAlert(channelNotification),
          ctaLabel: notificationMessage.actionLabel ?? '',
          ctaDestination: notificationMessage.metadata?.ctaDestination ?? '',
          timeSinceDisplayedSeconds: this._getTimeSinceDisplayed(
            channelNotification.id,
          ),
        }),
    });
  }

  trackNotificationToastExpired(
    toastCloseEvent: ToastCloseEvent,
    displayedAt: number,
  ): void {
    const message: ToastMessageOptions | undefined = toastCloseEvent.message;
    const channelNotification: ChannelNotification | undefined =
      message?.data?.channelNotification;

    if (!message?.id || !this._hasTrackableRule(channelNotification)) return;

    const timeDisplayedSeconds: number = Math.round(
      (Date.now() - displayedAt) / 1000,
    );
    const displayDurationSeconds: number = Math.round(message.life / 1000);

    this._trackBySeverity(channelNotification, {
      info: () =>
        this._notificationPreferencesAnalyticsService.trackNotificationToastExpired(
          {
            notification: this._toTrackingNotification(channelNotification),
            timeDisplayedSeconds,
            displayDurationSeconds,
          },
        ),
      alert: () =>
        this._notificationPreferencesAnalyticsService.trackAlertToastExpired({
          alert: this._toTrackingAlert(channelNotification),
          timeDisplayedSeconds,
          displayDurationSeconds,
        }),
    });
  }

  trackToastClicked(notificationMessage: INotificationMessage): void {
    const channelNotification: ChannelNotification | undefined =
      notificationMessage?.channelNotification;

    if (!this._hasTrackableRule(channelNotification)) return;

    const timeDisplayedSeconds: number = this._getTimeSinceDisplayed(
      channelNotification.id,
    );

    this._trackBySeverity(channelNotification, {
      info: () =>
        this._notificationPreferencesAnalyticsService.trackToastClicked({
          notification: this._toTrackingNotification(channelNotification),
          timeDisplayedSeconds,
        }),
      alert: () =>
        this._notificationPreferencesAnalyticsService.trackAlertToastClicked({
          alert: this._toTrackingAlert(channelNotification),
          timeDisplayedSeconds,
        }),
    });
  }

  private _hasTrackableRule(
    channelNotification: ChannelNotification | undefined,
  ): boolean {
    return !!(
      channelNotification?.id && channelNotification?.notificationRule?.severity
    );
  }

  private _trackBySeverity(
    channelNotification: ChannelNotification,
    handlers: { info: () => void; alert: () => void },
  ): void {
    switch (channelNotification.notificationRule.severity) {
      case NotificationRuleSeverityEnum.Info:
        handlers.info();
        break;
      case NotificationRuleSeverityEnum.Alert:
        handlers.alert();
        break;
    }
  }

  private _toTrackingNotification(
    channelNotification: ChannelNotification,
  ): ITrackingNotification {
    const rule: NotificationRule | undefined =
      channelNotification.notificationRule;

    return {
      id: channelNotification.id,
      notificationSlug: rule?.notificationSlug,
      name: rule?.name ?? '',
      description: rule?.description ?? '',
      severity: rule?.severity ?? NotificationRuleSeverityEnum.Info,
    };
  }

  private _toTrackingAlert(
    channelNotification: ChannelNotification,
  ): ITrackingAlert {
    const rule: NotificationRule | undefined =
      channelNotification.notificationRule;

    return {
      id: channelNotification.id,
      notificationSlug: rule?.notificationSlug,
      name: rule?.name ?? '',
      description: rule?.description ?? '',
      severity: rule?.severity ?? NotificationRuleSeverityEnum.Alert,
    };
  }

  private _getTimeSinceDisplayed(notificationId: string): number {
    const displayedAt: number | undefined =
      this._toastDisplayTimes.get(notificationId);

    if (!displayedAt) return 0;

    return Math.round((Date.now() - displayedAt) / 1000);
  }
}
